import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { KPICard, KPICardProps } from '../KPICard/KPICard';
import { LayoutGrid } from 'lucide-react';

// --- TYPE DEFINITIONS ---

interface KPICollectionCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  kpis?: KPICardProps[];
  variant?: 'card' | 'horizontal';
  columns?: 1 | 2 | 3 | 4;
  showTitle?: boolean;
  spacing?: 'compact' | 'normal';
  onKPIClick?: (kpiData: KPICardProps, index: number) => void;
}

// --- CONSTANTS ---

const GRID_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

// --- MAIN COMPONENT ---

const KPICollectionCard: React.FC<KPICollectionCardProps> = ({
  title,
  kpis = [],
  variant = 'card',
  columns = 3,
  showTitle = true,
  spacing = 'normal',
  onKPIClick,
  className = '',
  ...props
}) => {
  const isCard = variant === 'card';
  const spacingClass = spacing === 'compact' ? (isCard ? 'gap-3' : 'space-y-3') : (isCard ? 'gap-4' : 'space-y-4');

  const handleKPIClick = (kpiData: KPICardProps, index: number) => {
    onKPIClick?.(kpiData, index);
  };

  const renderKPIs = () => {
    if (kpis.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">
            <LayoutGrid className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-sm text-gray-500">No KPIs to display</p>
        </div>
      );
    }

    return kpis.map((kpi, index) => (
      <KPICard
        key={kpi.title || index} // Assuming title is unique, fallback to index
        {...kpi}
        variant={isCard ? 'card' : 'horizontal'}
        onClick={onKPIClick ? () => handleKPIClick(kpi, index) : undefined}
        className={isCard ? 'h-full' : ''}
      />
    ));
  };

  return (
    <Card
      className={cn('p-6', className)}
      role="region"
      aria-label={title || 'KPI Collection'}
      {...props}
    >
      {showTitle && title && (
        <CardHeader className="p-0 mb-6">
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="p-0">
        <div className={cn(isCard ? 'grid' : 'flex flex-col', isCard && GRID_COLUMNS[columns], spacingClass)}>
          {renderKPIs()}
        </div>
      </CardContent>
    </Card>
  );
};

KPICollectionCard.displayName = 'KPICollectionCard';

export { KPICollectionCard };
export type { KPICollectionCardProps };
